import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { BulkJobStatusResponse, RowJobStatus } from "@nexus/contracts"
import { toast } from "sonner"
import { useTranslation } from "react-i18next"
import { suggestScripts } from "../api/opportunities.api"
import { bulkJobStatusQueryOptions, opportunityKeys, revealGenerationTask } from "@/lib/queries"
import { Sentry } from "@/lib/sentry"
import { getLocalUserFacingApiMessage } from "@/lib/api-client"
import type { BulkActionState } from "./useOpportunityBulkAction"
import { SCRIPT_WRITING_PHASE } from "../constants/script-generation-phases"

/**
 * Drives the "Suggest Script" async job through the unified task-progress
 * sidebar — same channel as bulk generate-scripts.
 *
 * It POSTs `scripts/suggest`, persists the correlationId in sessionStorage
 * (survives refresh), polls the same `/status/:correlationId` endpoint via
 * `bulkJobStatusQueryOptions`, and keeps the scripts list fresh as results land.
 *
 * Custom generation has no mid-run cancellation; no cancel affordance is exposed.
 */

const MAX_JOB_AGE_MS = 15 * 60 * 1000

interface StoredJob {
  correlationId: string
  timestamp: number
  /** Requested variation count — the fallback "total" when the engine fails
   *  before reporting one, so a failure never reads as "0 succeeded · 0 failed". */
  variations: number
}

function storageKey(tenantId: string, setId: string) {
  return `suggest-scripts-job:${tenantId}:${setId}`
}

function saveJob(tenantId: string, setId: string, job: StoredJob) {
  try {
    sessionStorage.setItem(storageKey(tenantId, setId), JSON.stringify(job))
  } catch {
    /* sessionStorage unavailable (private mode) — degrade to in-memory only */
  }
}

function loadJob(tenantId: string, setId: string): StoredJob | null {
  try {
    const raw = sessionStorage.getItem(storageKey(tenantId, setId))
    if (!raw) return null
    return JSON.parse(raw) as StoredJob
  } catch {
    return null
  }
}

function clearJob(tenantId: string, setId: string) {
  try {
    sessionStorage.removeItem(storageKey(tenantId, setId))
  } catch {
    /* ignore */
  }
}

function isTerminal(status: string | undefined): boolean {
  return status === "COMPLETED" || status === "FAILED"
}

export interface SuggestScriptsJobVars {
  description: string
  variations: number
  referenceUrls: string[]
}

export function useSuggestScriptsJob({ tenantId, setId }: { tenantId: string; setId: string }) {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const [activeCorrelationId, setActiveCorrelationId] = useState<string | null>(null)
  const lastSeenCompletedRef = useRef(0)
  const invalidatedOnDoneRef = useRef(false)
  const requestedVariationsRef = useRef(0)
  const warnedRef = useRef(false)
  // True only while a job is actively polling. Guards against starting a second
  // concurrent job — but, unlike `hasActiveJob`, it flips back to false once the
  // job is terminal, so a completed/failed job never blocks the next submission.
  const isRunningRef = useRef(false)

  // Rehydrate an in-flight job on mount / when the set changes, so navigating
  // away and back (or a refresh) keeps showing progress.
  useEffect(() => {
    const stored = loadJob(tenantId, setId)
    if (stored && Date.now() - stored.timestamp <= MAX_JOB_AGE_MS) {
      setActiveCorrelationId(stored.correlationId)
      requestedVariationsRef.current = stored.variations ?? 0
    } else {
      if (stored) clearJob(tenantId, setId)
      setActiveCorrelationId(null)
    }
    lastSeenCompletedRef.current = 0
    invalidatedOnDoneRef.current = false
    warnedRef.current = false
  }, [tenantId, setId])

  const hasActiveJob = activeCorrelationId !== null

  const statusQuery = useQuery<BulkJobStatusResponse | null>({
    ...bulkJobStatusQueryOptions(activeCorrelationId ?? ""),
    enabled: hasActiveJob,
  })

  const state = useMemo((): BulkActionState => {
    if (!hasActiveJob) return { type: "idle" }

    if (statusQuery.isError) {
      return {
        type: "error",
        action: "generate-scripts",
        message: statusQuery.error?.message ?? "Polling failed",
      }
    }

    if (statusQuery.data === null) {
      return { type: "expired", action: "generate-scripts" }
    }

    const data = statusQuery.data

    if (data && isTerminal(data.status)) {
      // For the custom flow, `total`/`completed` are script counts (not
      // opportunities). Build a single-entry items map so the card's done
      // summary renders "scripts generated" / partial / all-failed correctly.
      const failedJob = data.status === "FAILED"
      const done = data.completed ?? 0
      // The engine can fail before it reports a total (e.g. prompt/context
      // resolution), so fall back to the requested variation count — otherwise
      // a hard failure misleadingly renders as "0 succeeded · 0 failed".
      const requested = data.total ?? requestedVariationsRef.current ?? 0
      const failed = failedJob
        ? Math.max(requested - done, 1) // a failed job always shows ≥1 failure
        : Math.max(0, requested - done)
      const total = Math.max(requested, done + failed)
      const items: Record<string, RowJobStatus> = {
        custom: done > 0 && !failedJob ? "done" : "failed",
      }
      return {
        type: "done",
        action: "generate-scripts",
        items,
        summary: { total, done, failed },
        scriptCount: done,
        cancelled: data.cancelled,
      }
    }

    // Active (still loading, ACCEPTED, or PROCESSING). The custom flow is a
    // single-phase pipeline — it only writes scripts — so always present it as
    // the writing phase with the requested count. This keeps the card on one
    // consistent "Writing scripts — N of M" view instead of flashing the shared
    // bulk 6-step checklist (Prepare brief → … → Write scripts), which doesn't
    // apply here and would otherwise show until the engine's first update lands.
    return {
      type: "polling",
      action: "generate-scripts",
      correlationId: activeCorrelationId!,
      items: {},
      total: data?.total ?? requestedVariationsRef.current,
      completed: data?.completed ?? 0,
      phase: data?.phase ?? SCRIPT_WRITING_PHASE,
    }
  }, [hasActiveJob, statusQuery.data, statusQuery.isError, statusQuery.error, activeCorrelationId])

  // Track whether a job is actively polling so the submit guard blocks only
  // genuinely concurrent runs — not a prior job whose correlationId still lingers
  // in `activeCorrelationId` (kept so the progress card can show its final state).
  useEffect(() => {
    isRunningRef.current = state.type === "polling"
  }, [state.type])

  // Refresh the scripts list as scripts land (incremental) and once on terminal,
  // so the new "Custom Scripts" group appears without a manual reload.
  useEffect(() => {
    if (state.type === "polling") {
      const completed = state.completed ?? 0
      if (completed > lastSeenCompletedRef.current) {
        lastSeenCompletedRef.current = completed
        void queryClient.invalidateQueries({ queryKey: opportunityKeys.scripts(setId) })
      }
    }
    if (state.type === "done" && !invalidatedOnDoneRef.current) {
      invalidatedOnDoneRef.current = true
      void queryClient.invalidateQueries({ queryKey: opportunityKeys.scripts(setId) })
    }
  }, [state, setId, queryClient])

  // Once terminal, drop the persisted job so a later refresh doesn't re-show a
  // stale card; the in-memory state stays until the card auto-dismisses or a new
  // job starts.
  useEffect(() => {
    if (state.type === "done" || state.type === "error" || state.type === "expired") {
      clearJob(tenantId, setId)
    }
  }, [state.type, tenantId, setId])

  // Warn once when the job reaches terminal status carrying unreadable
  // reference links — surfaced by the engine so users know scripts were
  // generated from the description alone for those links.
  useEffect(() => {
    const data = statusQuery.data
    if (!data || !isTerminal(data.status) || warnedRef.current) return
    const unreadable = data.unreadableUrls ?? []
    if (unreadable.length > 0) {
      warnedRef.current = true
      toast.warning(
        t("opportunities.scripts.suggest.unreadableLinks", {
          count: unreadable.length,
          urls: unreadable.join(", "),
        }),
      )
    }
  }, [statusQuery.data, t])

  const mutation = useMutation({
    mutationFn: async (vars: SuggestScriptsJobVars) => {
      if (isRunningRef.current) {
        throw new Error("A script suggestion is already in progress")
      }
      return suggestScripts(setId, vars.description, vars.variations, tenantId, vars.referenceUrls)
    },
    onSuccess: ({ correlationId }, variables) => {
      lastSeenCompletedRef.current = 0
      invalidatedOnDoneRef.current = false
      warnedRef.current = false
      requestedVariationsRef.current = variables.variations
      setActiveCorrelationId(correlationId)
      saveJob(tenantId, setId, { correlationId, timestamp: Date.now(), variations: variables.variations })
      revealGenerationTask(queryClient)
      void queryClient.invalidateQueries({ queryKey: opportunityKeys.bulkStatus(correlationId) })
    },
    onError: (err) => {
      Sentry.captureException(err)
      const message = getLocalUserFacingApiMessage(err)
      if (message) toast.error(message)
    },
    meta: { skipGlobalError: true },
  })

  const execute = useCallback(
    (vars: SuggestScriptsJobVars) => mutation.mutate(vars),
    [mutation],
  )

  return {
    state,
    execute,
    isSubmitting: mutation.isPending,
    isActive: hasActiveJob,
  }
}
